import React, { FC, ReactElement, ReactNode, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import { useTranslation } from 'react-i18next';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import { LocaleClassroomSettingProps } from '../../../../types';
import { Locale } from 'types/schema-generated-types';
import Config from 'config';

const { locales } = Config;

type CommonLocaleSettingProps = LocaleClassroomSettingProps & {
  mutation: any;
  description: string;
  formLabel?: ReactNode;
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: theme.typography.pxToRem(24),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    }
  };
});

const LocaleSetting: FC<CommonLocaleSettingProps> = ({
  classroom,
  expanded,
  setting: settingValue,
  handleChangeExpanded,
  panel,
  mutation,
  description,
  formLabel
}) => {
  const { t } = useTranslation('LanguageOptionSetting');
  const classes = useStyles();
  const { id: classroomId } = classroom;
  const [value, setValue] = useState(settingValue || Locale.EN_US);

  const [, { openAlert }] = useStore();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value as Locale);
  };

  const handleOnCancel = (): void => setValue(settingValue || Locale.EN_US);

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await mutation({
        variables: {
          data: {
            classroomId,
            value
          }
        }
      });
      openAlert(getSettingSuccessAlert('Language', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Language Option')}
      panel={panel}
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={description}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        {formLabel && (
          <FormLabel component="label" className={classes.label}>
            {formLabel}
          </FormLabel>
        )}
        <RadioGroup
          aria-label="language setting"
          name="languageSetting"
          value={value}
          onChange={handleChange}
        >
          {locales.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`language-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default LocaleSetting;
